import React, { useState, useEffect } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbPhone, TbMail, TbDeviceMobile, TbBrandWhatsapp } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ConfirmationModal from '../../../templates/ConfirmationModal';
import appConfig from '../../../../config/app-config';

// ✅ URL GENÉRICA - Funciona para PF e PJ
const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const BASE_URL = `${PERSON_API_URL}/person`;

interface Contact {
    id?: string;
    type: string;
    content: string;
    isActive: boolean;
    isDefault: boolean;
    isNationalNumber?: boolean;
}

interface Props {
    personId: string;  // ✅ UUID genérico (Individual ou Legal Person)
    personType?: 'individual' | 'legal_person';  // ✅ Para IDs únicos
    contacts?: Contact[];
    onContactsChange?: (contacts: Contact[]) => void;
}

const PersonFormContacts: React.FC<Props> = ({
    personId,
    personType = 'individual',
    contacts: initialContacts = [],
    onContactsChange = () => { }
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    const contacts = useSignal<Contact[]>([]);

    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [contactToDelete, setContactToDelete] = useState<string | null>(null);
    const [isContentFocused, setIsContentFocused] = useState<boolean>(false);
    const [form, setForm] = useState<Contact>({
        type: 'EMAIL',
        content: '',
        isActive: true,
        isDefault: false,
        isNationalNumber: false
    });

    const contactTypes = [
        { value: 'EMAIL', label: 'E-mail' },
        { value: 'PHONE', label: 'Telefone Fixo' },
        { value: 'MOBILE', label: 'Celular' },
        { value: 'WHATSAPP', label: 'WhatsApp' }
    ];

    const getContactIcon = (type: string) => {
        switch (type) {
            case 'EMAIL': return <TbMail className="text-blue-600" />;
            case 'PHONE': return <TbPhone className="text-green-600" />;
            case 'MOBILE': return <TbDeviceMobile className="text-purple-600" />;
            case 'WHATSAPP': return <TbBrandWhatsapp className="text-green-500" />;
            default: return <TbPhone className="text-gray-600" />;
        }
    };

    const getContactTypeLabel = (type: string) => {
        const typeObj = contactTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    useEffect(() => {
        if (initialContacts && initialContacts.length > 0) {
            contacts.value = initialContacts;
        }

        if (personId) {
            console.log('🔄 Carregando contatos para person:', personId);
            loadContacts();
        }
    }, [personId]);

    const openModal = (contact?: Contact) => {
        if (contact) {
            setEditingContact(contact);
            setForm(contact);
        } else {
            setEditingContact(null);
            setForm({
                type: 'EMAIL',
                content: '',
                isActive: true,
                isDefault: false,
                isNationalNumber: false
            });
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingContact(null);
        setForm({
            type: 'EMAIL',
            content: '',
            isActive: true,
            isDefault: false,
            isNationalNumber: false
        });
    };

    const validateForm = () => {
        if (!form.content.trim()) {
            toast.error('Preencha o conteúdo do contato');
            return false;
        }

        // Validação de email
        if (form.type === 'EMAIL') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(form.content)) {
                toast.error('Digite um e-mail válido');
                return false;
            }
        }

        // Validação de telefone/celular/whatsapp
        if (['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type)) {
            const phoneRegex = /^\d{10,15}$/;
            const cleanPhone = form.content.replace(/\D/g, '');
            if (!phoneRegex.test(cleanPhone)) {
                toast.error('Digite um telefone válido (10-15 dígitos)');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        isLoading.value = true;
        try {
            const url = editingContact
                ? `${BASE_URL}/${personId}/contacts/${editingContact.id}`
                : `${BASE_URL}/${personId}/contacts`;

            const method = editingContact ? 'PUT' : 'POST';

            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const payload = {
                ...form,
                is_active: form.isActive,
                is_default: form.isDefault,
                is_national_number: form.isNationalNumber
            };
            const { isActive, isDefault, isNationalNumber, id, ...cleanPayload } = payload;

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(cleanPayload)
            });

            if (response.ok) {
                toast.success(editingContact ? 'Contato atualizado!' : 'Contato cadastrado!');
                await loadContacts();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar contato');
            }
        } catch (error) {
            toast.error('Erro ao salvar contato');
            console.error('Error saving contact:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!contactToDelete) return;

        isLoading.value = true;
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${BASE_URL}/${personId}/contacts/${contactToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Contato excluído!');
                await loadContacts();
            } else {
                toast.error('Erro ao excluir contato');
            }
        } catch (error) {
            toast.error('Erro ao excluir contato');
            console.error('Error deleting contact:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setContactToDelete(null);
        }
    };

    const loadContacts = async () => {
        if (!personId) {
            console.log('⚠️ personId está vazio, não carregando contatos');
            return;
        }

        console.log('🔍 Carregando contatos para personId:', personId);
        isLoading.value = true;

        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${BASE_URL}/${personId}/contacts`, {
                headers: {
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                contacts.value = data.contacts || data || [];
                console.log('✅ Contatos carregados:', contacts.value.length);
            } else {
                const errorText = await response.text();
                console.error('❌ Erro na resposta:', response.status, response.statusText, errorText);
            }
        } catch (error) {
            console.error('❌ Error loading contacts:', error);
            contacts.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const confirmDelete = (contactId: string) => {
        setContactToDelete(contactId);
        isDeleteModalOpen.value = true;
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbPhone className="text-blue-600" />
                    Contatos
                </h3>
                <button
                    id={`btn-add-contact-${personType}`}  // ✅ ID único por tipo
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
                >
                    <TbPlus size={16} />
                    Novo Contato
                </button>
            </div>

            <div className="bg-white rounded-lg shadow">
                {isLoading.value ? (
                    <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-sm">Carregando contatos...</p>
                    </div>
                ) : contacts.value.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbPhone size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum contato cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Contato</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {contacts.value.map((contact, index) => (
                                    <tr key={contact.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {getContactIcon(contact.type)}
                                                <span className="font-medium">{getContactTypeLabel(contact.type)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <div className="font-medium">{contact.content}</div>
                                                {contact.isDefault && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        Principal
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${contact.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                {contact.isActive ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    id={`btn-edit-contact-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => openModal(contact)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    id={`btn-remove-contact-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => confirmDelete(contact.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingContact ? 'Editar Contato' : 'Novo Contato'}
                        </h3>

                        <div className="space-y-4">
                            <SelectTextField
                                id={`contact-type-${personType}`}  // ✅ ID único
                                label="Tipo de Contato *"
                                value={form.type}
                                onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                                items={contactTypes}
                            />

                            <TextEdit
                                id={`contact-content-${personType}`}  // ✅ ID único
                                label="Contato *"
                                value={form.content}
                                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                                onFocus={() => setIsContentFocused(true)}
                                onBlur={() => setIsContentFocused(false)}
                                placeholder={
                                    form.type === 'EMAIL' ? 'exemplo@email.com' :
                                        ['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type) ? '(11) 99999-9999' : ''
                                }
                            />

                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        id={`contact-active-${personType}`}  // ✅ ID único
                                        type="checkbox"
                                        checked={form.isActive}
                                        onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Ativo
                                </label>

                                <label className="flex items-center">
                                    <input
                                        id={`contact-default-${personType}`}  // ✅ ID único
                                        type="checkbox"
                                        checked={form.isDefault}
                                        onChange={(e) => setForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Principal
                                </label>
                            </div>

                            {['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type) && (
                                <label className="flex items-center">
                                    <input
                                        id={`contact-national-${personType}`}  // ✅ ID único
                                        type="checkbox"
                                        checked={form.isNationalNumber}
                                        onChange={(e) => setForm(prev => ({ ...prev, isNationalNumber: e.target.checked }))}
                                        className="mr-2"
                                    />
                                    Número nacional
                                </label>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                id={`btn-cancel-contact-${personType}`}  // ✅ ID único
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                id={`btn-save-contact-${personType}`}  // ✅ ID único
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setContactToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Contato"
                id={contactToDelete || ''}
                text="Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default PersonFormContacts;
